/** The draw-mode HUD: floating bottom-center pill with live stats, history
 * controls, and the DONE/discard flows — the phone's route-editing toolbar,
 * in First Light. Purely presentational: App.tsx owns the planner reducer
 * and persistence, this just renders `plan`/`stats` and calls back on every
 * action (same split as StatePicker/LayersPanel). The one bit of local
 * state is the DONE name-input; onNamingChange mirrors it out so App's map
 * handlers can ignore clicks while the input is open (same stray-click
 * hazard as the discard confirm). */
import { useEffect, useRef, useState } from "react";
import { formatDuration, formatFeet, formatGainFt, formatMiles } from "../plan/geo";
import type { DayStat } from "../plan/daystats";
import type { ClosureWarning } from "../plan/seasons";
import type { Plan, PlanStats } from "../plan/types";
import type { SamplingState } from "../App";

/** "Route Aug 25" — the DONE prefill when a plan has never been named. */
function defaultName(): string {
  return `Route ${new Date().toLocaleDateString("en-US", { month: "short", day: "numeric" })}`;
}

export default function PlanBar({
  plan,
  stats,
  sampling,
  days,
  canUndo,
  canRedo,
  onUndo,
  onRedo,
  profileOpen,
  onToggleProfile,
  selected,
  onDeleteSelected,
  dayBreakState,
  onToggleDayBreak,
  confirmDiscard,
  onRequestClose,
  onConfirmDiscard,
  onCancelDiscard,
  onDone,
  onNamingChange,
  requestDoneNonce,
  snapMode,
  snapAvailable,
  onToggleSnap,
  solving,
  longSolving,
  onReverse,
  onOutAndBack,
  closures,
  gradeOn,
  gradeAvailable,
  onToggleGrade,
  suggestedName,
  fuelGapM,
  onOpenItinerary,
}: {
  plan: Plan;
  stats: PlanStats;
  /** Terrain-sampling lifecycle — see App.tsx's `SamplingState` doc comment.
   * Drives the gain/loss/ETA stats between "…" placeholders, dimmed
   * last-known values, and a normal read. */
  sampling: SamplingState;
  /** Per-day breakdown (plan/daystats.ts) — a second stats row renders
   * once there are ≥2 days (i.e. ≥1 day break set); a single-day plan has
   * nothing the main stats row doesn't already say. */
  days: DayStat[];
  canUndo: boolean;
  canRedo: boolean;
  onUndo: () => void;
  onRedo: () => void;
  profileOpen: boolean;
  onToggleProfile: () => void;
  /** Index of the selected control point, or null — drives the contextual
   * delete row above the main bar. */
  selected: number | null;
  onDeleteSelected: () => void;
  /** The selected point's day-break eligibility/state — "ineligible" (an
   * endpoint, or nothing selected) hides the toggle entirely rather than
   * showing a control that would just no-op. */
  dayBreakState: "none" | "set" | "ineligible";
  onToggleDayBreak: () => void;
  confirmDiscard: boolean;
  onRequestClose: () => void;
  onConfirmDiscard: () => void;
  onCancelDiscard: () => void;
  onDone: (name: string) => void;
  onNamingChange?: (naming: boolean) => void;
  /** Bumped by App on double-click-to-finish and Enter-to-finish — a nonce
   * rather than a boolean so two finish gestures in a row (open naming,
   * cancel, finish again) both register as fresh requests. */
  requestDoneNonce: number;
  /** Whether NEW placements magnetize onto the legal network — App's
   * global toggle, iOS parity with the phone's straightMode. */
  snapMode: boolean;
  /** False when this state serves no routing graph/snap grid — the
   * control goes inert rather than lying about what it can do. */
  snapAvailable: boolean;
  onToggleSnap: () => void;
  /** True while ≥1 leg solve is in flight — drives the SOLVING… chip. */
  solving: boolean;
  /** True once a leg's search has run past the 12s fast window and is
   * still genuinely searching in the background (App's onSettled-driven
   * counter, router.ts's own two-phase contract) — drives the dim SOLVING
   * LONG ROUTE… chip, but only while `solving` above is false: the two
   * chips would otherwise both show for the SAME leg during its first 12s,
   * saying almost the same thing. Honesty rule: this is only ever true
   * while a search is genuinely still in flight, never left on after. */
  longSolving?: boolean;
  /** Reverse/mirror the whole route in place — the ⋯ overflow menu's two
   * actions. Both call straight into the existing plannerCore reducers;
   * PlanBar owns none of that logic, only the menu's open/closed state. */
  onReverse: () => void;
  onOutAndBack: () => void;
  /** Seasonal closures this plan's snapped legs traverse (plan/seasons.ts)
   * — empty hides the SEASONAL chip entirely. */
  closures: ClosureWarning[];
  /** Whether the map's route line is colored by grade instead of the plain
   * trace color — App's persisted toggle. */
  gradeOn: boolean;
  /** False under 2 terrain samples — nothing to classify a grade from yet,
   * so the control goes inert rather than lying about what it can do. */
  gradeAvailable: boolean;
  onToggleGrade: () => void;
  /** The dominant legal-route name across this plan's snapped legs (App's
   * derived signal, itself sourced from router.ts's per-leg
   * SolvedLeg.name) — a PREFILL ONLY for the DONE naming field the first
   * time it opens on a plan that's never been named; a plan with an
   * existing name always keeps it (see openNaming below). null/undefined
   * when nothing solved carries a name yet, in which case openNaming falls
   * back to defaultName() exactly as it always has. */
  suggestedName?: string | null;
  /** Longest gap between this plan's Fuel waypoints, meters (plan/fuel.ts)
   * — drives the FUEL GAP chip; absent/null hides it entirely (no Fuel
   * waypoints on this plan, or fewer than 2 route points to project onto). */
  fuelGapM?: number | null;
  /** Opens ui/ItinerarySheet.tsx (App.tsx owns that overlay, same split as
   * every other PlanBar-triggered sheet) — the button itself is hidden
   * entirely under 2 points rather than merely disabled, since there's no
   * day to show yet (plan/itinerary.ts's own dayStats-length-0 contract). */
  onOpenItinerary: () => void;
}) {
  const [naming, setNamingState] = useState(false);
  const [name, setName] = useState("");
  const [showOverflow, setShowOverflow] = useState(false);
  // Focused the moment the menu opens so its own onKeyDown (below) is
  // guaranteed to be in the bubble path for Escape — mirrors the naming
  // input's autoFocus, same reasoning: without a focus target inside it,
  // Escape would keydown on whatever HAD focus (the ⋯ toggle, outside this
  // menu) and fall through to App's global handler instead.
  const overflowFirstBtnRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (showOverflow) overflowFirstBtnRef.current?.focus();
  }, [showOverflow]);

  // The SEASONAL chip's list panel — same anchor-menu idiom and the same
  // focus-for-Escape reasoning as showOverflow/overflowFirstBtnRef above;
  // its rows are informational (see the panel's own render below), so the
  // CLOSE button both closes the panel and doubles as that focus target.
  const [showClosures, setShowClosures] = useState(false);
  const closuresCloseBtnRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (showClosures) closuresCloseBtnRef.current?.focus();
  }, [showClosures]);

  const setNaming = (v: boolean) => {
    setNamingState(v);
    onNamingChange?.(v);
  };
  // The bar unmounts with the whole plan dock on exit — without this reset
  // a naming=true left behind would silently eat every map click of the
  // NEXT draw session.
  useEffect(() => () => onNamingChange?.(false), []); // eslint-disable-line react-hooks/exhaustive-deps

  const openNaming = () => {
    // plan.name wins when this plan's already been named (re-saving keeps
    // it); otherwise the network's own suggestion beats the generic
    // "Route Aug 25" default, when there is one. Either way it's a
    // PREFILL, not a lock-in — the input's onFocus below selects it so
    // typing replaces rather than appends, same contract regardless of
    // which of the three sources filled it.
    setName(plan.name || suggestedName || defaultName());
    setNaming(true);
  };

  const submitName = () => {
    onDone(name.trim() || plan.name || defaultName());
    setNaming(false);
  };

  // requestDoneNonce carries over from a PRIOR draw session (App state, not
  // reset on exit) — a fresh mount must treat whatever value it sees at
  // mount as the baseline, not as a request. Only a change AFTER mount
  // (this session's own dblclick/Enter) opens naming.
  const mountNonceRef = useRef(requestDoneNonce);
  useEffect(() => {
    if (requestDoneNonce !== mountNonceRef.current) {
      openNaming();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestDoneNonce]);

  if (confirmDiscard) {
    return (
      <div className="plan-bar plan-bar-confirm">
        <span className="plan-bar-confirm-label">Discard route?</span>
        <button
          className="plan-bar-btn plan-bar-btn-danger"
          onClick={onConfirmDiscard}
          title="Discard this route"
        >
          DISCARD
        </button>
        <button className="plan-bar-btn" onClick={onCancelDiscard} title="Go back to drawing">
          KEEP DRAWING
        </button>
      </div>
    );
  }

  if (naming) {
    return (
      <form
        className="plan-bar plan-bar-naming"
        role="dialog"
        aria-modal="true"
        aria-labelledby="plan-bar-naming-heading"
        onSubmit={(e) => {
          e.preventDefault();
          submitName();
        }}
      >
        <span id="plan-bar-naming-heading" className="sr-only">
          Save route
        </span>
        <input
          autoFocus
          // The prefill ("Route Aug 25" or the existing name) is a
          // suggestion — select it on focus so typing replaces rather than
          // appends, the standard prefill contract.
          onFocus={(e) => e.currentTarget.select()}
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Escape") setNaming(false);
          }}
          placeholder={defaultName()}
        />
        <button type="submit" className="plan-bar-btn plan-bar-btn-primary" title="Save this route">
          SAVE
        </button>
      </form>
    );
  }

  const gradeLabel =
    stats.maxGradePct !== null && stats.maxGradePct >= 1
      ? `MAX ${Math.round(stats.maxGradePct)}% GRADE`
      : null;

  // Placeholder vs. stale-but-shown: a debounce/fetch in flight with no
  // resolved samples yet reads as "…"; the same pending state with a prior
  // resolved set (e.g. a drag's mid-move re-solve) keeps showing the
  // last-good numbers, just dimmed — rendering nothing either way is what
  // the UX review flagged as reading like breakage.
  const pendingFresh = sampling === "pending" && stats.gainM === null;
  const pendingStale = sampling === "pending" && stats.gainM !== null;
  const staleClass = (base: string) => (pendingStale ? `${base} plan-bar-stat-stale` : base);

  // Tier 2 (WARNINGS) gate — severity color (the ember chips below) shows
  // up ONLY here, never in the Tier 1 facts row. Mirrors the old nesting
  // exactly: both chips previously lived inside the same `points.length ===
  // 0 ? hint : (...)` branch as the facts, so neither ever rendered against
  // an empty route — this gate keeps that.
  const hasWarnings = plan.points.length > 0 && (closures.length > 0 || fuelGapM != null);

  // "D1 3.2 mi · +480 ft · ~1:40" — distance always real (dayStats' own
  // doc), gain/eta only once that day has enough samples to derive them;
  // loss is deliberately left out here (unlike the main row) to keep a
  // multi-day list from getting too busy to scan at a glance.
  function dayLine(d: DayStat): string {
    const parts = [formatMiles(d.distanceM)];
    if (d.gainM !== null) parts.push(formatGainFt(d.gainM));
    if (d.etaSeconds !== null) parts.push(formatDuration(d.etaSeconds));
    return `D${d.day} ${parts.join(" · ")}`;
  }

  return (
    <>
      {selected !== null && (
        <div className="plan-bar-context">
          <span className="plan-bar-context-label">POINT {selected + 1}</span>
          {dayBreakState !== "ineligible" && (
            <button
              className={dayBreakState === "set" ? "plan-bar-btn active" : "plan-bar-btn"}
              onClick={onToggleDayBreak}
              title={
                dayBreakState === "set"
                  ? "Remove the overnight stop at this point"
                  : "End the day at this point (overnight stop)"
              }
            >
              {dayBreakState === "set" ? "REMOVE DAY BREAK" : "END DAY HERE"}
            </button>
          )}
          <button
            className="plan-bar-btn plan-bar-btn-danger"
            onClick={onDeleteSelected}
            title="Delete this point (Delete)"
          >
            DELETE
          </button>
          <span className="plan-bar-context-caption">esc deselects</span>
        </div>
      )}
      {showOverflow && (
        <div
          className="anchor-menu"
          onKeyDown={(e) => {
            // stopPropagation, not just handling it here, is load-bearing:
            // without it this Escape still bubbles to App's window-level
            // draw-mode listener, which — seeing no point selected — would
            // ALSO fire requestClose (the discard-confirm prompt) in the
            // same keystroke. See overflowFirstBtnRef's comment for why
            // focus is guaranteed to be inside this menu already.
            if (e.key === "Escape") {
              e.stopPropagation();
              setShowOverflow(false);
            }
          }}
        >
          <button
            ref={overflowFirstBtnRef}
            className="anchor-menu-btn"
            disabled={plan.points.length < 2}
            onClick={() => {
              onReverse();
              setShowOverflow(false);
            }}
            title="Reverse direction (swap start and end)"
          >
            REVERSE
          </button>
          <button
            className="anchor-menu-btn"
            disabled={plan.points.length < 2}
            onClick={() => {
              onOutAndBack();
              setShowOverflow(false);
            }}
            title="Mirror the route back to its start"
          >
            OUT & BACK
          </button>
        </div>
      )}
      {showClosures && (
        <div
          className="anchor-menu"
          onKeyDown={(e) => {
            // Same load-bearing stopPropagation as showOverflow's menu
            // above — without it, Escape falls through to App's
            // window-level draw-mode listener and ALSO fires requestClose.
            if (e.key === "Escape") {
              e.stopPropagation();
              setShowClosures(false);
            }
          }}
        >
          <div className="anchor-menu-title">SEASONAL CLOSURES</div>
          {closures.map((c) => (
            <div
              key={c.id}
              className={c.openNow === false ? "closure-row closure-row-danger" : "closure-row"}
            >
              <span className="closure-row-name">{c.name}</span>
              <span className="closure-row-window">
                {c.openNow === false
                  ? `CLOSED NOW — ${c.window}`
                  : c.openNow === true
                    ? `OPEN ${c.window}`
                    : c.window}
              </span>
            </div>
          ))}
          <button
            ref={closuresCloseBtnRef}
            className="anchor-menu-btn anchor-menu-btn-cancel"
            onClick={() => setShowClosures(false)}
          >
            CLOSE
          </button>
        </div>
      )}
      <div className="plan-bar">
        <div className="plan-bar-stats">
          {plan.points.length === 0 ? (
            <span className="plan-bar-hint">TAP THE MAP TO START</span>
          ) : (
            <>
              <span className="plan-bar-stat">{formatMiles(stats.distanceM)}</span>
              {pendingFresh && (
                <>
                  <span className="plan-bar-stat plan-bar-stat-dim">…</span>
                  <span className="plan-bar-stat plan-bar-stat-dim">…</span>
                </>
              )}
              {stats.gainM !== null && (
                <span className={staleClass("plan-bar-stat")}>{formatGainFt(stats.gainM)}</span>
              )}
              {stats.lossM !== null && (
                <span className={staleClass("plan-bar-stat")}>−{formatFeet(stats.lossM)}</span>
              )}
              {stats.etaSeconds !== null && (
                <span className={staleClass("plan-bar-stat")}>{formatDuration(stats.etaSeconds)}</span>
              )}
              {gradeLabel && (
                <span className={staleClass("plan-bar-stat plan-bar-stat-grade")}>{gradeLabel}</span>
              )}
              {solving && <span className="plan-bar-stat plan-bar-stat-dim">SOLVING…</span>}
              {/* Never both at once — longSolving is only worth a word once
                * the fast window's own SOLVING… chip above has nothing left
                * to say (see this prop's doc comment for the honesty rule). */}
              {!solving && longSolving && (
                <span className="plan-bar-stat plan-bar-stat-dim">SOLVING LONG ROUTE…</span>
              )}
            </>
          )}
        </div>
        {/* Tier 2 WARNINGS — mounted only while there's an actual warning;
          * severity (ember) color lives here alone, never in the facts row
          * above. */}
        {hasWarnings && (
          <div className="plan-bar-stats plan-bar-warnings">
            {closures.length > 0 && (
              <button
                className="plan-bar-chip-closures"
                onClick={() => setShowClosures((v) => !v)}
                title="Seasonal closures this route crosses"
                aria-label={`${closures.length} seasonal closure${closures.length === 1 ? "" : "s"} on this route`}
                aria-haspopup="true"
                aria-expanded={showClosures}
              >
                ⚠ {closures.length} SEASONAL
              </button>
            )}
            {fuelGapM != null && (
              <span className={staleClass("plan-bar-chip-fuel")}>FUEL GAP {formatMiles(fuelGapM)}</span>
            )}
          </div>
        )}
        {days.length >= 2 && (
          <div className="plan-bar-stats">
            {/* .plan-bar-stat-day (secondary-row dim) and .plan-bar-stat-stale
              * (mid-solve dim) are mutually exclusive here, never both — see
              * .plan-bar-stat-day's own comment in theme.css for why nesting
              * them used to compound into a contrast failure. */}
            {days.map((d) => (
              <span
                key={d.day}
                className={`plan-bar-stat ${pendingStale ? "plan-bar-stat-stale" : "plan-bar-stat-day"}`}
              >
                {dayLine(d)}
              </span>
            ))}
          </div>
        )}
        <div className="plan-bar-actions">
          <button className="plan-bar-btn" disabled={!canUndo} onClick={onUndo} title="Undo">
            UNDO
          </button>
          <button className="plan-bar-btn" disabled={!canRedo} onClick={onRedo} title="Redo">
            REDO
          </button>
          <button
            className={snapMode ? "plan-bar-btn active" : "plan-bar-btn"}
            disabled={!snapAvailable}
            onClick={onToggleSnap}
            title={snapAvailable ? "Snap new points to the legal network" : "No routing data here"}
            aria-pressed={snapMode}
          >
            {snapMode ? "SNAP" : "STRAIGHT"}
          </button>
          <button
            className={profileOpen ? "plan-bar-btn active" : "plan-bar-btn"}
            onClick={onToggleProfile}
            title="Elevation profile"
            aria-pressed={profileOpen}
          >
            PROFILE
          </button>
          <button
            className={gradeOn ? "plan-bar-btn active" : "plan-bar-btn"}
            disabled={!gradeAvailable}
            onClick={onToggleGrade}
            title={gradeAvailable ? "Color the route by grade" : "No elevation data here yet"}
            aria-pressed={gradeOn}
          >
            GRADE
          </button>
          <button
            className={showOverflow ? "plan-bar-btn active" : "plan-bar-btn"}
            onClick={() => setShowOverflow((v) => !v)}
            title="More route actions"
            aria-label="More route actions"
            aria-haspopup="true"
            aria-expanded={showOverflow}
          >
            ⋯
          </button>
          {plan.points.length >= 2 && (
            <button
              className="plan-bar-btn"
              onClick={onOpenItinerary}
              title="Day-by-day itinerary"
            >
              ITINERARY
            </button>
          )}
          <button
            className="plan-bar-btn plan-bar-btn-primary"
            // Under 2 points there's no route to save — PlansSheet already
            // treats ≥2 as "a real route" for the draft-resume row; saving
            // less would pollute the list with unopenable empties.
            disabled={plan.points.length < 2}
            onClick={openNaming}
            // Opens the naming form — SAVE (above) is what actually saves.
            title="Name and save this route"
          >
            DONE
          </button>
          <button
            className="plan-bar-btn plan-bar-close"
            onClick={onRequestClose}
            aria-label="Close"
            // Esc closes the waypoint menu first, then deselects a point (App
            // .tsx's draw-mode key handler); only with nothing selected does
            // it close the editor — say so, or the hint contradicts the
            // "esc deselects" caption two buttons away.
            title="Close route editor (Esc with nothing selected)"
          >
            ✕
          </button>
        </div>
      </div>
    </>
  );
}
